import React, { useState } from 'react';
import { useAuth } from '../../authContext';

// Custom Hook importieren
import { useUsers } from '../users/useUsers';

// Shared Components importieren
import Modal from '../shared/Modal';
import StatusBadge from '../shared/StatusBadge';
import DataTable from '../shared/DataTable';
import ConfirmModal from '../shared/ConfirmModal';

// Lokale Komponente für das Benutzerformular
import UserForm from '../users/UserForm';

const Users = () => {
  // Custom Hook nutzen
  const { role } = useAuth();
  const {
    users,
    loading,
    error,
    fetchUsers,
    addUser,
    editUser,
    toggleUserStatus,
    getDatabaseBackup,
  } = useUsers();

  // Modal-State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // State für ausgewählten Benutzer für Status-Änderung
  const [selectedUserForToggle, setSelectedUserForToggle] = useState(null);

  // State für Formular-Daten (Benutzer)
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    role: 'Normal',
    active: true,
  });
  const [editingUser, setEditingUser] = useState(null);

  // --- Backup-Funktion ---
  const downloadBackup = async () => {
    try {
      const backupSql = await getDatabaseBackup();
      const blob = new Blob([backupSql], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${new Date().toISOString().slice(0, 10)}.sql`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setErrorModalMessage(err.message);
      setShowErrorModal(true);
      console.error('Fehler beim Backup-Download:', err);
    }
  };

  // Funktion zum Öffnen des Status-Toggle-Dialogs
  const openToggleStatusModal = (userId, currentStatus) => {
    setSelectedUserForToggle({ id: userId, currentStatus });
    setShowToggleStatusModal(true);
  };

  // Status-Toggle (nutzt den Hook)
  const handleToggleStatus = async () => {
    if (!selectedUserForToggle) return;
    try {
      await toggleUserStatus(selectedUserForToggle.id, selectedUserForToggle.currentStatus);
      setShowToggleStatusModal(false);
    } catch (err) {
      setErrorModalMessage(err.message);
      setShowErrorModal(true);
      setShowToggleStatusModal(false);
    }
  };

  // Neuer Benutzer: Speichern (nutzt den Hook)
  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await addUser(newUser);
      setShowAddModal(false);
      setNewUser({
        username: '',
        password: '',
        email: '',
        role: 'Normal',
        active: true,
      });
    } catch (err) {
      setErrorModalMessage(err.message);
      setShowErrorModal(true);
    }
  };

  // Benutzer bearbeiten: Speichern (nutzt den Hook)
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await editUser(editingUser.id, editingUser);
      setShowEditModal(false);
    } catch (err) {
      setErrorModalMessage(err.message);
      setShowErrorModal(true);
    }
  };

  // Tabellen-Spalten für DataTable
  const columns = [
    { key: 'username', header: 'Benutzername', render: (user) => user.username },
    { key: 'email', header: 'E-Mail', render: (user) => user.email },
    { key: 'role', header: 'Rolle', render: (user) => user.role },
    {
      key: 'active',
      header: 'Status',
      render: (user) => (
        <StatusBadge
          value={user.active}
          type="status"
          onClick={() => openToggleStatusModal(user.id, user.active)}
        />
      ),
    },
    {
      key: 'actions',
      header: 'Aktionen',
      render: (user) => (
        <button
          className="action-button edit"
          onClick={() => {
            setEditingUser({ ...user, password: '' }); // Passwort leer für Bearbeitung
            setShowEditModal(true);
          }}
        >
          ✏️ Bearbeiten
        </button>
      ),
    },
  ];

  // --- Render ---
  return (
    <div className="users-tab">
      {/* Toolbar mit "Neuer Benutzer"-Button und Backup-Button (nur für Admins) */}
      <div className="toolbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button
            className="add-button"
            onClick={() => {
              setNewUser({
                username: '',
                password: '',
                email: '',
                role: 'Normal',
                active: true,
              });
              setShowAddModal(true);
            }}
          >
            ➕ Neuer Benutzer
          </button>
          {role === 'Admin' && (
            <button
              className="export-button"
              onClick={downloadBackup}
            >
              💾 Backup (SQL)
            </button>
          )}
        </div>
      </div>

      {/* Tabelle oder Lade-/Fehlermeldung */}
      {loading ? (
        <p>Lade Benutzer...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <DataTable columns={columns} data={users} keyField="id" />
      )}

      {/* Modal: Neuer Benutzer */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Neuer Benutzer"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowAddModal(false)}>
              Abbrechen
            </button>
            <button type="submit" className="save-button" onClick={handleAddUser}>
              Speichern
            </button>
          </div>
        }
      >
        <form onSubmit={handleAddUser}>
          <UserForm data={newUser} onChange={setNewUser} />
        </form>
      </Modal>

      {/* Modal: Benutzer bearbeiten */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Benutzer bearbeiten"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowEditModal(false)}>
              Abbrechen
            </button>
            <button type="submit" className="save-button" onClick={handleEditUser}>
              Speichern
            </button>
          </div>
        }
      >
        {editingUser && (
          <form onSubmit={handleEditUser}>
            <UserForm data={editingUser} onChange={setEditingUser} isEditMode={true} />
          </form>
        )}
      </Modal>

      {/* Modal: Status ändern */}
      <ConfirmModal
        isOpen={showToggleStatusModal}
        onClose={() => setShowToggleStatusModal(false)}
        onConfirm={handleToggleStatus}
        title="Status ändern"
        message={
          <>
            Möchten Sie den Benutzer <strong>{users.find((u) => u.id === selectedUserForToggle?.id)?.username}</strong>
            wirklich auf <strong>{selectedUserForToggle?.currentStatus ? 'Inaktiv' : 'Aktiv'}</strong> setzen?
          </>
        }
        confirmText="Ja, ändern"
      />

      {/* Modal: Fehlermeldung */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Fehler"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowErrorModal(false)}>
              OK
            </button>
          </div>
        }
      >
        <p>{errorModalMessage}</p>
      </Modal>
    </div>
  );
};

export default Users;